import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DialogType } from '../../common/enums';
import {
  barringServicesMsg,
  editBarringServicesMsg,
  instructionsForBarringsMsg,
  instructionsMsg,
  t,
} from '../../common/i18n';
import { getAddonAssociationsAndDependencies, getAvailableAddOnCodes } from '../../common/utils/subscriptionUtils';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { openDialog } from '../../public/site/dialogStore.js';
import { editSection } from '../../selfservice/actions';
import { BarringsEditor } from '../BarringsContent/BarringsEditor';
import { BarringsView } from '../BarringsContent/BarringsView';
import { resolveBarringGroupAvailabilities } from '../BarringsContent/barringUtil';
import { DetailsWithCard } from '../DetailsWithInfoColumn/DetailsWithInfoColumn';
import { HeaderWithEditButton } from '../HeaderWithButton/HeaderWithButton';
import type { DialogParams } from '../../common/types/dialog';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { Subscription } from '../../generated/api/subscription';

import type { State } from '../../selfservice/common/store.js';

import './MobileSubscriptionBarringServices.scss';

export interface MobileSubscriptionBarringServicesProps {
  subscription: Subscription;
  hasPendingActions: boolean;
  addOnRules?: AddOnRulesResponse;
  addOnRulesMobilePbx?: AddOnRulesResponse;
}

export const MobileSubscriptionBarringServices = ({
  subscription,
  hasPendingActions,
  addOnRules,
  addOnRulesMobilePbx,
}: MobileSubscriptionBarringServicesProps) => {
  const dispatch = useDispatch();
  const { editingSection, saving } = useSelector((state: State) => ({
    editingSection: state?.selfservice?.pendingSubscriptionActions?.editingSection || '',
    saving: state?.selfservice?.pendingSubscriptionActions?.saving,
  }));
  const [isEditing, setEditing] = useState(editingSection === 'addonsBarrings');
  const { associations, dependencies } = getAddonAssociationsAndDependencies(
    subscription,
    addOnRules,
    addOnRulesMobilePbx
  );
  const availableAddOnCodes = getAvailableAddOnCodes(associations, subscription.commercialProductId);
  const barringGroupAvailabilities = resolveBarringGroupAvailabilities(availableAddOnCodes);

  const onShowDialog = (params: DialogParams) => openDialog(params);
  const onEditSectionIfNoActionsPending = () => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return false;
    }
    if (hasPendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return false;
    }
    dispatch(editSection('addonsBarrings'));
    return true;
  };

  const onEditClick = () => {
    if (onEditSectionIfNoActionsPending()) {
      setEditing(true);
    }
  };
  return (
    <DetailsWithCard
      cardContent={
        <>
          <h3>{t.RD4R(instructionsMsg)}</h3>
          <p>{t.BID9(instructionsForBarringsMsg)}</p>
        </>
      }
    >
      <HeaderWithEditButton
        title={t.ZUCA(barringServicesMsg)}
        showButton={!isEditing}
        buttonText={t.PTWI(editBarringServicesMsg)}
        onClick={onEditClick}
      />
      {isEditing ? (
        <BarringsEditor
          addOnAssociations={associations}
          addOnDependencies={dependencies}
          barringGroupAvailabilities={barringGroupAvailabilities}
          details={subscription.details!}
          subscription={subscription}
          onEditCompleted={() => setEditing(false)}
        />
      ) : (
        <BarringsView
          barringGroupAvailabilities={barringGroupAvailabilities}
          details={subscription.details!}
          disableEditing={saving ?? false}
          onEditWanted={onEditClick}
          showEditButton={false}
        />
      )}
    </DetailsWithCard>
  );
};
